import React, { useState } from 'react'

import { Button, Select, TextInput } from 'slab'

export const ACCESS_REASONS = {
  PLAN_SELECTION_CALL: "I'm conducting a plan selection support call with the member.",
  PLAN_SELECTION_CALL_FOLLOW_UP: "I'm doing follow-up work from a plan selection support call with the member.",
  OTHER: 'Other',
}
export const MIN_LENGTH_OF_OTHER_REASON_TEXT = 16

interface IBrokersMembersViewReasonForAccessProps {
  disabled?: boolean
  numberOfDaysOfAccess: number
  onSubmitReasonForAccess: (reasonForAccess: string) => void
}

const BrokersMembersViewReasonForAccess = ({
  disabled = false,
  numberOfDaysOfAccess,
  onSubmitReasonForAccess,
}: IBrokersMembersViewReasonForAccessProps) => {
  const [selectedReason, setSelectedReason] = useState(ACCESS_REASONS.PLAN_SELECTION_CALL)
  const [otherReasonText, setOtherReasonText] = useState('')

  const isOtherSelected = selectedReason === ACCESS_REASONS.OTHER
  const isOtherReasonLongEnough = otherReasonText.length >= MIN_LENGTH_OF_OTHER_REASON_TEXT

  const onChangeOtherReasonText = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOtherReasonText(e.target.value)
  }

  const handleReasonOptionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedReason(e.target.value)
  }

  const handleSubmit = () => {
    const submittedReason = isOtherSelected ? otherReasonText : selectedReason
    onSubmitReasonForAccess(submittedReason)
  }

  const submitDisabled = disabled || (
    isOtherSelected && !isOtherReasonLongEnough
  )

  return (
    <div className='w-full flex flex-col items-center'>
      <div className='flex flex-col gap-4 max-w-xl text-gray-700'>
        <h1 className='heading-lg'>Must provide reason for access</h1>
        <p>
          The page you wish to access contains sensitive information.
          Because of this, you have to provide business justification
          prior to accessing the page.
        </p>
        <p>
          Once approved, you'll be able to access the page for
          {' '}{numberOfDaysOfAccess} day(s) before having to request
          access again.
        </p>
        <p>
          Please select one of the following:
        </p>
        <div className='flex flex-col gap-1'>
          <Select
            onChange={handleReasonOptionChange}
            value={selectedReason}
          >
            {Object.values(ACCESS_REASONS).map((reason) => (
              <option key={reason} value={reason}>
                {reason}
              </option>
            ))}
          </Select>
          <TextInput
            disabled={!isOtherSelected}
            onChange={onChangeOtherReasonText}
            placeholder='Enter reason'
            value={otherReasonText}
          />
          {isOtherSelected && !isOtherReasonLongEnough && (
            <p className='ml-1 text-xs text-red-400'>
              "Other" reason must be at least {MIN_LENGTH_OF_OTHER_REASON_TEXT} characters long
            </p>
          )}
        </div>
        <Button
          disabled={submitDisabled}
          onClick={handleSubmit}
          size='large'
          variant='primary'
        >
          Submit access request
        </Button>
      </div>
    </div>
  )
}

export default BrokersMembersViewReasonForAccess
